import React, { useState, useEffect, useCallback } from "react";
import { Pagination } from "react-bootstrap";
import { debounce } from "lodash";
import { getConversationsAdminAPI } from "../reduxData/user/userAction";
import CreateConversationModal from "../Modals/CreateConversationModal";
import ViewConversationModal from "./ViewConversationModal";

const ManageConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });
  const [isLoading, setIsLoading] = useState(false);

  const [searchUsername, setSearchUsername] = useState("");
  const [searchItemName, setSearchItemName] = useState("");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  const fetchConversations = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const params = {
          page,
          limit: 15,
          participantUsername: searchUsername.trim(),
          itemName: searchItemName.trim(),
        };
        const data = await getConversationsAdminAPI(params);
        if (data) {
          setConversations(data.conversations);
          setPagination(data.pagination);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [searchUsername, searchItemName]
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleViewConversation = (id) => {
    setSelectedConversationId(id);
    setShowViewModal(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchConversations(1);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
      fetchConversations(pageNumber);
    }
  };

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h2 onClick={() => console.log(conversations)}>
            Manage Conversations
          </h2>
          <button
            className="btn btn-warning"
            onClick={() => setShowCreateModal(true)}
          >
            Create Conversation
          </button>
        </div>
        <div className="card-body">
          <form onSubmit={handleSearch} className="row g-3 mb-4">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Search by participant username..."
                value={searchUsername}
                onChange={(e) => setSearchUsername(e.target.value)}
              />
            </div>
            <div className="col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Search by item name..."
                value={searchItemName}
                onChange={(e) => setSearchItemName(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <button type="submit" className="btn btn-primary w-100">
                Search
              </button>
            </div>
          </form>

          {isLoading ? (
            <p>Loading conversations...</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>Participants</th>
                    <th>Related Item</th>
                    <th>Last Updated</th>
                    <th>Admin Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {conversations.map((convo) => (
                    <tr key={convo._id} style={{ verticalAlign: "middle" }}>
                      <td>
                        {convo.participants.map((p) => (
                          <div
                            key={p._id}
                            className="d-flex align-items-center mb-1"
                          >
                            <img
                              src={
                                p.profile_pic ||
                                "https://placehold.co/40x40/eee/ccc?text=User"
                              }
                              alt={p.user_name}
                              className="rounded-circle me-2"
                              style={{
                                width: "30px",
                                height: "30px",
                                objectFit: "cover",
                              }}
                              onClick={() => console.log(convo)}
                            />
                            <span>{p.user_name}</span>
                          </div>
                        ))}
                      </td>

                      <td>
                        {convo.relatedItem ? (
                          <div className="d-flex align-items-center">
                            <img
                              src={
                                convo.relatedItem.thumbnails?.[0] ||
                                "https://placehold.co/40x40/eee/ccc?text=Item"
                              }
                              alt={convo.relatedItem.item_name}
                              className="rounded me-2"
                              style={{
                                width: "40px",
                                height: "40px",
                                objectFit: "cover",
                              }}
                            />
                            <span>{convo.relatedItem.item_name}</span>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>

                      <td>{new Date(convo.updatedAt).toLocaleString()}</td>
                      <td>{convo.createdByAdmin ? "Yes" : "No"}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => handleViewConversation(convo._id)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && pagination.totalPages > 1 && (
            <div className="d-flex justify-content-center">
              <Pagination>
                <Pagination.First
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                />

                {[...Array(pagination.totalPages).keys()].map((number) => {
                  const pageNumber = number + 1;
                  if (
                    pageNumber > pagination.currentPage + 2 ||
                    pageNumber < pagination.currentPage - 2
                  ) {
                    return null;
                  }
                  return (
                    <Pagination.Item
                      key={pageNumber}
                      active={pageNumber === pagination.currentPage}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </Pagination.Item>
                  );
                })}

                <Pagination.Next
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                />
                <Pagination.Last
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                />
              </Pagination>
            </div>
          )}
        </div>
      </div>

      <CreateConversationModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={() => fetchConversations(pagination.currentPage)}
      />

      <ViewConversationModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        conversationId={selectedConversationId}
      />
    </div>
  );
};

export default ManageConversations;
